import type { RenderableDocumentAnnotation } from '@st/document-components/annotations'
import { isEmpty, JsonMap, JSONValue } from '@st/util/json-value'
import type { Folder, STCategory, STChecklistItem, STDocument, STDocumentType } from '@st/sdk'
import type { FormConfig } from '@st/ui-config'
import { deepEqual } from './util'
import type { FormationLayout, ResolvedFormationPage } from './formation-layout'

export type FormationFieldComment = {
  key: string
  author?: string
  time?: string
  body: string
}

type FormationFieldDecoration = {
  hint?: string
  backgroundColor?: string
  borderColor?: string
  borderWidth?: number
}

export const DEFAULT_FORMATION_FIELD_DECORATION: FormationFieldDecoration = {
  backgroundColor: '#f2f4fd'
}

export type FormationData = {
  formConfig: FormConfig
  inputs: JsonMap
  settings?: JsonMap
  priorInputs?: JsonMap
  fieldHints: Record<string, string>
  fieldComments: FormationFieldComment[]
  annotations: RenderableDocumentAnnotation[]
  highlightedPageIds: string[]
}

type FormationFieldView = {
  value: JSONValue
  decoration: FormationFieldDecoration
  comment?: FormationFieldComment
}

export type FormationPageFields = Record<string, FormationFieldView>

export type FormationPageView = {
  config: ResolvedFormationPage
  fields: FormationPageFields
}

export type FormationFieldChange = (key: string, value: JSONValue) => void

/**
 * Projects a resolved layout into renderer-owned plain data. Keeping this
 * boundary free of application hooks and context lets the same Formation
 * components render through the interactive HTML and graphics/PDF paths.
 */
export function resolveFormationPageViews({
  layout,
  inputs,
  priorInputs = {},
  fieldHints,
  fieldComments
}: Pick<FormationData, 'inputs' | 'priorInputs' | 'fieldHints' | 'fieldComments'> & {
  layout: FormationLayout
}): FormationPageView[] {
  const commentsByKey = Object.fromEntries(fieldComments.map((comment) => [comment.key, comment]))

  return layout.pages.map((config) => ({
    config,
    fields: Object.fromEntries(
      config.fieldKeys.map((key) => [
        key,
        {
          value: inputs[key],
          decoration: formationFieldDecoration({
            hint: fieldHints[key],
            curValue: inputs[key],
            priorValue: priorInputs[key]
          }),
          comment: commentsByKey[key]
        }
      ])
    )
  }))
}

export type ChecklistData = {
  entityName: string
  folder: Folder
  checklistItems: STChecklistItem[]
  documents: STDocument[]
  documentTypes: STDocumentType[]
  categories: STCategory[]
}

function formationFieldDecoration({
  hint,
  curValue,
  priorValue
}: {
  hint: string | undefined
  curValue: JSONValue
  priorValue: JSONValue | undefined
}) {
  if (isEmpty(curValue)) {
    return { hint, backgroundColor: '#f2f4fd' }
  } else if (deepEqual(curValue, priorValue)) {
    return {
      hint,
      backgroundColor: '#e0fbee',
      borderColor: '#5abe8e',
      borderWidth: 0.5
    }
  } else {
    return {
      hint,
      backgroundColor: '#ffffe3',
      borderColor: '#daa00b',
      borderWidth: 0.5
    }
  }
}
